import React, { memo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CREATOR_METRICS, CREATOR_FEED } from "./data";

const formatValue = (value, format, suffix) => {
  let out;
  if (format === "compact") {
    out =
      value >= 1000
        ? `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`
        : `${value}`;
  } else if (format === "decimal") {
    out = value.toFixed(1);
  } else {
    out = Math.round(value).toString();
  }
  return `${out}${suffix || ""}`;
};

const useCountUp = (target, active, duration = 1500) => {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!active) {
      setValue(0);
      return undefined;
    }
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, active, duration]);
  return value;
};

const MetricCard = ({ metric, active, index }) => {
  const value = useCountUp(metric.value, active, 1400 + index * 120);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
      transition={{ delay: index * 0.08 }}
      className="rounded-xl border border-white/10 bg-white/5 p-3"
    >
      <p className="text-[10px] uppercase tracking-wider text-white/40">{metric.label}</p>
      <p className="mt-1 text-lg font-bold text-white">
        {formatValue(value, metric.format, metric.suffix)}
      </p>
    </motion.div>
  );
};

const TAG_STYLE = {
  deal: "text-orange-400",
  auto: "text-blue-400",
  insight: "text-green-400",
  growth: "text-pink-400",
  lead: "text-amber-400",
};

const CreatorDashboard = ({ active }) => {
  const [feed, setFeed] = useState(CREATOR_FEED.slice(0, 3));
  const cursor = useRef(3);

  useEffect(() => {
    if (!active) {
      setFeed(CREATOR_FEED.slice(0, 3));
      cursor.current = 3;
      return undefined;
    }
    const interval = setInterval(() => {
      const next = CREATOR_FEED[cursor.current % CREATOR_FEED.length];
      cursor.current += 1;
      setFeed((prev) => [{ ...next, key: cursor.current }, ...prev].slice(0, 4));
    }, 2200);
    return () => clearInterval(interval);
  }, [active]);

  return (
    <div className="mx-auto grid h-full w-full max-w-md grid-cols-1 gap-3 overflow-y-auto overflow-x-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]/90 p-3.5 shadow-2xl backdrop-blur sm:grid-cols-5 sm:overflow-hidden sm:p-5 [scrollbar-width:none]">
      {/* Metrics */}
      <div className="min-w-0 sm:col-span-3">
        <p className="mb-2 text-[10px] uppercase tracking-wider text-white/40 sm:mb-3 sm:text-xs">Creator overview</p>
        <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
          {CREATOR_METRICS.map((m, i) => (
            <MetricCard key={m.id} metric={m} active={active} index={i} />
          ))}
        </div>
      </div>

      {/* Live feed */}
      <div className="min-w-0 sm:col-span-2">
        <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40 sm:mb-3 sm:text-xs">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" /> Live
        </p>
        <div className="flex flex-col gap-2">
          <AnimatePresence initial={false}>
            {feed.map((item) => (
              <motion.div
                key={item.key || item.id}
                layout
                initial={{ opacity: 0, x: 20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 26 }}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] leading-snug text-white/80"
              >
                <span className={`mr-1 ${TAG_STYLE[item.tag] || "text-white/60"}`}>●</span>
                {item.text}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default memo(CreatorDashboard);
